import React from 'react';
import { Box, Typography, Grid2, Container, Divider, useTheme, IconButton, Icon } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

const Footer = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToSection: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleInicioClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLegalPageClick = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const linkCategories = [
    {
      title: 'La App',
      links: [
        { label: '¿Qué es Mi Rifa?', section: 'que-es' },
        { label: 'Descarga Oficial', section: 'descarga-app' },
        { label: 'Registro Rápido', section: 'registro' },
      ],
    },
    {
      title: 'Funciones',
      links: [
        { label: 'Crear tu Rifa', section: 'crea-tu-rifa' },
        { label: 'Vender Boletos', section: 'vender-boleto' },
        { label: 'Gestionar Estados', section: 'cambiar-estado' },
        { label: 'Estadísticas en Vivo', section: 'estadisticas' },
        { label: 'Personalizar Tablero', section: 'personalizar' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
            : 'linear-gradient(145deg, #14173e 0%, #1c215e 45%, #2d3484 100%)',
        color: 'white',
        pt: { xs: 7, md: 8 },
        pb: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Círculos decorativos sutiles */}
      <Box
        sx={{
          position: 'absolute',
          width: 380,
          height: 380,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          top: -120,
          right: -80,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Contenido Principal */}
        <Grid2 container spacing={{ xs: 4, md: 5 }} sx={{ mb: 5 }}>
          {/* Logo y Descripción */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Box
              onClick={handleInicioClick}
              sx={{ cursor: 'pointer', display: 'inline-block', mb: 2, transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.02)' } }}
            >
              <img
                src="/assets/imgs/logo.png"
                alt="Mi Rifa App Logo"
                style={{ height: '54px' }}
                loading="lazy"
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.75)',
                lineHeight: 1.7,
                fontSize: '0.92rem',
                mb: 3,
                maxWidth: 340,
              }}
            >
              La plataforma líder para crear, administrar y comercializar sorteos de manera profesional, transparente y en tiempo real.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, color: 'rgba(255,255,255,0.75)' }}>
              <Icon sx={{ color: theme.palette.secondary.main, fontSize: 20 }}>security</Icon>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Plataforma 100% Segura y Verificada
              </Typography>
            </Box>
          </Grid2>

          {/* Categorías de Enlaces */}
          {linkCategories.map((category) => (
            <Grid2 key={category.title} size={{ xs: 6, sm: 3, md: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  mb: 2.2,
                  fontSize: '1rem',
                  color: '#ffffff',
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -6,
                    left: 0,
                    width: 28,
                    height: 3,
                    background: theme.palette.secondary.main,
                    borderRadius: '2px',
                  },
                }}
              >
                {category.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {category.links.map((link) => (
                  <Typography
                    key={link.section}
                    variant="body2"
                    onClick={() => scrollToSection(link.section)}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.75)',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#ffffff',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Box>
            </Grid2>
          ))}

          {/* Enlaces Legales */}
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                mb: 2.2,
                fontSize: '1rem',
                color: '#ffffff',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -6,
                  left: 0,
                  width: 28,
                  height: 3,
                  background: theme.palette.secondary.main,
                  borderRadius: '2px',
                },
              }}
            >
              Legal y Enlaces
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: 3 }}>
              <Typography
                variant="body2"
                onClick={handleInicioClick}
                sx={{
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': { color: '#ffffff', transform: 'translateX(4px)' },
                }}
              >
                Inicio
              </Typography>
              <Typography
                variant="body2"
                onClick={() => handleLegalPageClick('/politicas-privacidad')}
                sx={{
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': { color: '#ffffff', transform: 'translateX(4px)' },
                }}
              >
                Políticas de Privacidad
              </Typography>
              <Typography
                variant="body2"
                onClick={() => handleLegalPageClick('/terminos-y-condiciones')}
                sx={{
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': { color: '#ffffff', transform: 'translateX(4px)' },
                }}
              >
                Términos y Condiciones
              </Typography>
            </Box>

            {/* Redes Sociales y Descarga Rápida */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {[
                { href: 'https://www.facebook.com/rauhtech', src: '/assets/imgs/facebook.png', alt: 'Facebook' },
                { href: 'https://www.instagram.com/rauhtech', src: '/assets/imgs/instagram.png', alt: 'Instagram' },
                { href: 'https://www.tiktok.com/@rauhtech', src: '/assets/imgs/tiktok.png', alt: 'TikTok' },
              ].map((social, index) => (
                <IconButton
                  key={index}
                  component="a"
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    p: 0.8,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <img src={social.src} alt={social.alt} style={{ height: '24px', width: '24px' }} />
                </IconButton>
              ))}
            </Box>
          </Grid2>
        </Grid2>

        {/* Divider */}
        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

        {/* Bottom Footer */}
        <Grid2 container spacing={2} alignItems="center">
          <Grid2 size={{ xs: 12, md: 7 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.85rem',
                textAlign: { xs: 'center', md: 'left' },
                display: 'block',
              }}
            >
              © {new Date().getFullYear()} Mi Rifa App. Todos los derechos reservados.
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-end' },
                gap: 1.2,
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                Desarrollado por
              </Typography>
              <a
                href="https://www.rauhtech.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.06)',
                      filter: 'brightness(1.2)',
                    },
                  }}
                >
                  <img
                    src="/assets/imgs/logo_rauh.png"
                    alt="RAUH TECH Ltda"
                    style={{ height: '34px' }}
                  />
                </Box>
              </a>
            </Box>
          </Grid2>
        </Grid2>
      </Container>
    </Box>
  );
};

export default Footer;